/**
 * Isometric projection maths (doc 10 §Style, doc 16 Law L1).
 *
 * Tiles are 2:1 diamonds, 32x16 internal pixels, fixed forever — zoom changes
 * the blit scale S, never the tile size.
 *
 * The anchor of anything placed on the grid is the GROUND-PLANE CENTRE of its
 * footprint. Sprites are built upward from it. Nothing hangs downward.
 */

export const TILE_W = 32;
export const TILE_H = 16;
/** Half-tile: one step in i moves +TW in x and +TH in y. */
export const TW = TILE_W / 2; // 16
export const TH = TILE_H / 2; // 8

export interface Point {
  x: number;
  y: number;
}

/** Screen position of the north corner of tile (0,0). */
export interface IsoOrigin {
  ox: number;
  oy: number;
}

export const ORIGIN_ZERO: IsoOrigin = { ox: 0, oy: 0 };

/** North (top) corner of tile (i, j). */
export function tileCorner(i: number, j: number, o: IsoOrigin = ORIGIN_ZERO): Point {
  return { x: o.ox + (i - j) * TW, y: o.oy + (i + j) * TH };
}

/** L1: ground-plane centre of tile (i, j). */
export function tileCentre(i: number, j: number, o: IsoOrigin = ORIGIN_ZERO): Point {
  return { x: o.ox + (i - j) * TW, y: o.oy + (i + j) * TH + TH };
}

/**
 * L1: ground-plane centre of a w x d block whose north-most tile is (i, j).
 *
 * Doc 16 states this as `(OX+(i-j)*TW, OY+(i+j)*TH + (w+d)*TH/2)`, which is
 * exact for square footprints (every shipped footprint is square: 1x1, 2x2,
 * 3x3). The `(w-d)*TW/2` term below is the general case, and is zero whenever
 * w === d, so the two agree wherever the game actually uses it.
 */
export function blockCentre(
  i: number,
  j: number,
  w: number,
  d: number,
  o: IsoOrigin = ORIGIN_ZERO,
): Point {
  return {
    x: o.ox + (i - j) * TW + ((w - d) * TW) / 2,
    y: o.oy + (i + j) * TH + ((w + d) * TH) / 2,
  };
}

/** Half-extents of a footprint diamond, in internal pixels. */
export function footprintHalfW(w: number, d: number): number {
  return ((w + d) * TW) / 2;
}
export function footprintHalfH(w: number, d: number): number {
  return ((w + d) * TH) / 2;
}

/** Fractional tile coordinates under a screen point (picking). */
export function screenToTile(x: number, y: number, o: IsoOrigin = ORIGIN_ZERO): Point {
  const dx = (x - o.ox) / TW;
  const dy = (y - o.oy) / TH;
  return { x: (dx + dy) / 2, y: (dy - dx) / 2 };
}

/** Painter's-algorithm depth key: tiles further from the camera draw first. */
export function depthKey(i: number, j: number): number {
  return i + j;
}

export interface Span {
  y: number;
  x0: number;
  x1: number;
}

/**
 * Scanline spans of a square footprint diamond of `n` tiles per side, centred
 * on (cx, cy). Widths step 4 px per row (the crisp 2:1 staircase) and tile
 * diamonds interlock exactly: a diamond is 32n-2 px at its widest inside a 32n
 * pitch, and the neighbours at (+-TW, +-TH) fill the 1 px margins.
 */
export function diamondSpans(cx: number, cy: number, n: number): Span[] {
  if (!Number.isInteger(n) || n < 1) throw new Error(`footprint must be a positive integer, got ${n}`);
  const half = TH * n; // rows above centre
  const rows = half * 2;
  const spans: Span[] = [];
  for (let r = 0; r < rows; r++) {
    const k = r < half ? r : rows - 1 - r;
    const hw = 2 * k + 1;
    spans.push({ y: cy - half + r, x0: cx - hw, x1: cx + hw - 1 });
  }
  return spans;
}

/** Square-footprint guard: everything the game places is square (doc 03/04/05). */
export function requireSquareFootprint(w: number, d: number): number {
  if (w !== d) {
    throw new Error(`non-square footprint ${w}x${d}: iso primitives support square footprints only`);
  }
  return w;
}
